import { router } from "@inertiajs/vue3";

const _queue: Array<(urlParams: URLSearchParams) => URLSearchParams> = [];

function _visitNext(): void {
    const func = _queue[0];
    if (!func) {
        return;
    }

    const currentParams = new URLSearchParams(window.location.search);
    const currentSearch = currentParams.toString();
    const updatedParams = func(currentParams);
    const updatedSearch = updatedParams.toString();

    if (currentSearch === updatedSearch) {
        // Nothing changed; can skip the call to router.visit()
        _queue.shift();
        return _visitNext();
    }

    const url = `${window.location.pathname}?${updatedParams.toString()}`;

    // Note that we have to use Inertia's router.visit() instead of native APIs or
    // useUrlSearchParams because we need to preserve the Inertia state.
    router.visit(url, {
        preserveState: true,
        replace: true,
        only: [],
        onFinish: () => {
            setTimeout(() => {
                _queue.shift();
                _visitNext();
            });
        },
    });
}

/**
 * Enqueues a URL search param update to be run at the next safe opportunity.  The callback function
 * `updateFunc` will receive the latest URL search params and should return an updated search params
 * object with the desired changes.
 *
 * Explanation: Inertia's `router.visit` and `router.replace` execute asynchronously, so when
 * multiple locations in the code are using them to update the browser location bar simultaneously,
 * the resulting race conditions can sometimes clobber the desired changes.  This function
 * guarantees that search param updates will be run sequentially.
 */
export function enqueueURLParamsUpdate(updateFunc: (urlParams: URLSearchParams) => URLSearchParams) {
    _queue.push(updateFunc);
    if (_queue.length === 1) {
        setTimeout(() => _visitNext());
    }
}
